import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DoxMascot } from "@/components/Logo";
import { Reveal } from "./Reveal";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      setResult(data);
      toast.success("Message received — Dox is on it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-surface/30 border-t border-white/5 overflow-hidden">
      <div className="glow-orb w-[460px] h-[460px] left-1/4 top-0 bg-dox-magenta/12" />
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-dox-orange mb-5">Message Dox</div>
          <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.05]">
            Ask Dox’s team <span className="text-gradient-brand">anything.</span>
          </h2>
          <p className="mt-6 text-lg text-white/60 leading-relaxed max-w-md">
            Questions about compliance, onboarding or how DOX works? Send a message — Dox logs every
            request, assigns a tracking reference, and a human follows up when needed.
          </p>
          <div className="mt-8 flex items-center gap-3 text-white/50 text-sm">
            <Mail size={16} className="text-dox-orange" />
            support@ezdoxai.com
          </div>
          <div className="mt-10 hidden sm:block">
            <DoxMascot size={120} />
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          {result ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-strong rounded-2xl border border-white/10 p-8 text-center"
              data-testid="contact-success"
            >
              <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
              <h3 className="font-display font-bold text-2xl mt-5">Dox is on it.</h3>
              <p className="text-white/60 mt-3">{result.message}</p>
              <div className="mt-6 inline-flex flex-col items-center rounded-xl bg-white/5 border border-white/10 px-6 py-4">
                <span className="text-xs text-white/40 uppercase tracking-wider">Tracking reference</span>
                <span className="font-mono font-bold text-lg text-dox-orange" data-testid="contact-tracking-ref">{result.tracking_ref}</span>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="glass-strong rounded-2xl border border-white/10 p-7 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name">
                  <input data-testid="contact-name" required value={form.name} onChange={upd("name")} className="dox-input" placeholder="Your name" />
                </Field>
                <Field label="Email">
                  <input data-testid="contact-email" required type="email" value={form.email} onChange={upd("email")} className="dox-input" placeholder="you@company.com" />
                </Field>
              </div>
              <Field label="Subject">
                <input data-testid="contact-subject" value={form.subject} onChange={upd("subject")} className="dox-input" placeholder="What's this about?" />
              </Field>
              <Field label="Message">
                <textarea data-testid="contact-message" required rows={4} value={form.message} onChange={upd("message")} className="dox-input resize-none" placeholder="Tell Dox what you need…" />
              </Field>
              <button
                data-testid="contact-submit"
                disabled={loading}
                className="w-full rounded-full bg-dox-orange py-3.5 font-bold text-white flex items-center justify-center gap-2 hover:bg-dox-orangeHover disabled:opacity-60 transition-all"
              >
                {loading ? "Sending…" : <>Send to Dox <Send size={16} /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>

      <style>{`
        .dox-input{width:100%;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.1);border-radius:12px;padding:12px 14px;color:#fff;font-size:14px;outline:none;transition:border-color .2s, box-shadow .2s;}
        .dox-input::placeholder{color:rgba(255,255,255,0.3);}
        .dox-input:focus{border-color:#1E78FF;box-shadow:0 0 0 3px rgba(30,120,255,0.15);}
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
